"use client"

import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { cn } from "@/utils/utils"

import ChevronDown from "@/assets/images/chevron-down.svg"
import ChevronDownDark from "@/assets/images/chevron-down-dark.svg"
import { cva, VariantProps } from "class-variance-authority"

const accordionVariants = cva(
    "my-3 overflow-hidden bg-[var(--surface-default)]",
    {
        variants: {
            variant: {
                default: "border-b last:border-b-0  border-b-[var(--outline-default-variant)] hover:border-b-[var(--outline-default-variant)] disabled:border-b-[var(--outline-disabled)]",
                container: "shadow-lg rounded-lg border last:border-0 border-[var(--outline-default-variant)] hover:border-[var(--outline-default-variant)] disabled:border-[var(--outline-disabled)]"
            },
        },
        defaultVariants: {
            variant: "default"
        },
    }
)

function Accordion({
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
    return <AccordionPrimitive.Root data-slot="accordion" {...props} />
}

function AccordionItem({
    className,
    variant,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item> &
    VariantProps<typeof accordionVariants>) {
    return (
        <AccordionPrimitive.Item
            data-slot="accordion-item"
            className={cn(accordionVariants({ variant, className }))}
            {...props}
        />
    )
}

function AccordionTrigger({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
    return (
        <AccordionPrimitive.Header className="flex group">
            <AccordionPrimitive.Trigger
                data-slot="accordion-trigger"
                className={cn("",
                    "focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 text-left text-base font-medium transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>img]:rotate-180 group py-2",
                    "hover:bg-[var(--surface-default-variant)]",
                    "text-[var(--on-surface-default)]",
                    className
                )}
                {...props}
            >
                <div className="w-full">
                    <div className={cn("mx-6 py-4 flex flex-1 items-start justify-between gap-4 text-[var(--on-surface-default)]"
                    )}>
                        {children}
                        <img
                            className="w-10 pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200 group-data-[state=open]:rotate-180 place-self-center dark:hidden"
                            src={ChevronDown}
                        />
                        <img
                            className="w-10 pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200 group-data-[state=open]:rotate-180 place-self-center hidden dark:block"
                            src={ChevronDownDark}
                        />
                    </div>
                </div>
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header >
    )
}

function AccordionContent({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
    return (
        <AccordionPrimitive.Content
            data-slot="accordion-content"
            aria-label={children?.toString()}
            className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm mx-6 text-[var(--on-surface-default)] group:hover:bg-[var(--surface-default-variant)]"
            {...props}
        >
            <div aria-label={children?.toString()} className={cn("pt-2 pb-6 text-sm ", className)}>{children}</div>
        </AccordionPrimitive.Content>
    )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
